import { useEffect, useState } from "react";
import { measureHeroCrop, toggleHeroDebug, type HeroCropReadout } from "@/lib/hero-debug";

interface Props {
  /** The cropped frame element (the `overflow-hidden` wrapper). */
  frameRef: React.RefObject<HTMLElement>;
  /** The `object-fit: cover` image inside the frame. */
  imgRef: React.RefObject<HTMLImageElement>;
  /** Shown in the readout so multiple portraits can be told apart. */
  label?: string;
}

/**
 * QA-only overlay drawn on top of a hero portrait. Shows:
 *  • crosshair + dot at the resolved anchor point
 *  • rule-of-thirds grid and the frame outline (crop bounds in the frame)
 *  • a readout of anchor %, zoom, frame size and the visible source region
 *
 * Purely visual, `pointer-events: none` except the small close button, and
 * never rendered unless hero debug mode is on.
 */
export const HeroDebugOverlay = ({ frameRef, imgRef, label = "hero" }: Props) => {
  const [data, setData] = useState<HeroCropReadout | null>(null);

  useEffect(() => {
    let raf = 0;
    const measure = () => {
      raf = 0;
      const frame = frameRef.current;
      const img = imgRef.current;
      if (!frame || !img) return;
      setData(measureHeroCrop(img, frame));
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };

    schedule();
    window.addEventListener("resize", schedule);
    window.addEventListener("scroll", schedule, { passive: true });
    const ro = new ResizeObserver(schedule);
    if (frameRef.current) ro.observe(frameRef.current);
    const img = imgRef.current;
    img?.addEventListener("load", schedule);

    return () => {
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", schedule);
      ro.disconnect();
      img?.removeEventListener("load", schedule);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [frameRef, imgRef]);

  const x = data?.anchorX ?? 50;
  const y = data?.anchorY ?? 50;

  return (
    <div className="pointer-events-none absolute inset-0 z-30 font-mono">
      {/* Crop bounds — the live frame edge the image is cropped to */}
      <div className="absolute inset-0 border-2 border-dashed border-[#00e5ff]/80" />

      {/* Rule-of-thirds guides */}
      {[33.333, 66.666].map((p) => (
        <div key={`v${p}`} className="absolute top-0 bottom-0 w-px bg-[#00e5ff]/25" style={{ left: `${p}%` }} />
      ))}
      {[33.333, 66.666].map((p) => (
        <div key={`h${p}`} className="absolute left-0 right-0 h-px bg-[#00e5ff]/25" style={{ top: `${p}%` }} />
      ))}

      {/* Anchor crosshair */}
      <div className="absolute top-0 bottom-0 w-px bg-[#ff2d78]" style={{ left: `${x}%` }} />
      <div className="absolute left-0 right-0 h-px bg-[#ff2d78]" style={{ top: `${y}%` }} />
      <div
        className="absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[#ff2d78] bg-[#ff2d78]/30"
        style={{ left: `${x}%`, top: `${y}%` }}
      />

      {/* Readout */}
      <div className="absolute left-2 top-2 max-w-[calc(100%-1rem)] rounded bg-black/80 px-2 py-1.5 text-[10px] leading-[1.5] text-[#c8faff] shadow">
        <div className="text-[#ff9ec4]">
          {label} · {data?.breakpoint ?? "—"}
        </div>
        <div>
          anchor {x.toFixed(1)}% / {y.toFixed(1)}%
        </div>
        <div>zoom ×{(data?.scale ?? 1).toFixed(3)}</div>
        <div>
          frame {Math.round(data?.frameW ?? 0)}×{Math.round(data?.frameH ?? 0)}
        </div>
        <div>
          src {data?.naturalW ?? 0}×{data?.naturalH ?? 0}
        </div>
        <div>
          crop {(data?.cropLeftPct ?? 0).toFixed(1)}%,{(data?.cropTopPct ?? 0).toFixed(1)}% ·{" "}
          {(data?.cropWidthPct ?? 100).toFixed(1)}×{(data?.cropHeightPct ?? 100).toFixed(1)}%
        </div>
      </div>

      <button
        type="button"
        onClick={toggleHeroDebug}
        className="pointer-events-auto absolute right-2 top-2 rounded bg-black/80 px-2 py-1 text-[10px] uppercase tracking-wider text-[#c8faff] hover:bg-black"
      >
        Hide (Shift+H)
      </button>
    </div>
  );
};
